/**
 * Tab "Actividades" del evento.
 *
 * Lista categorías (con sus actividades) y permite a los admins:
 *  - Sembrar categorías estándar (FIEL CONQUISTADOR).
 *  - CRUD categorías y actividades.
 *  - Capturar puntajes por club.
 *  - Ver ranking de actividades.
 *
 * Directores ven todo en modo lectura.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import {
  Card,
  CardContent,
  Badge,
  Button,
  EmptyState,
  Skeleton,
  useConfirm,
} from "@/components/ui";
import {
  Sparkles,
  Plus,
  Edit2,
  Trash2,
  Trophy,
  Activity as ActivityIcon,
  ChevronDown,
  ChevronRight,
  Info,
  Upload,
  Download,
  FileDown,
  FileCheck,
  Send,
  Link as LinkIcon,
} from "lucide-react";
import { useAuthStore } from "@/stores/authStore";
import { Can } from "@/components/auth/Can";
import {
  downloadActivityScoreSheet,
  downloadAllActivityScoreSheets,
} from "@/lib/pdf/activityScoreSheetPdf";
import {
  useEventSubmissions,
  useClubSubmissions,
} from "@/hooks/useActivitySubmissions";
import { SubmissionFormModal } from "./SubmissionFormModal";
import { SubmissionsMatrixPanel } from "./SubmissionsMatrixPanel";
import { toast } from "react-hot-toast";
import {
  useEventActivityCategories,
  useEventActivities,
  useEventActivityScores,
} from "@/hooks/useEventActivities";
import { useClubs } from "@/hooks/useOrganization";
import {
  deleteCategory,
  deleteActivity,
  deleteScore,
  seedStandardCategories,
  SCORE_MODE_LABEL,
} from "@/lib/services/eventActivities";
import { SUBMISSION_STATUS_LABEL } from "@/lib/services/activitySubmissions";
import {
  CategoryFormModal,
  ActivityFormModal,
  ActivityScoreModal,
  ActivityBatchScoreModal,
  CsvImportModal,
} from "./ActivityModals";
import { EventActivityRankingPanel } from "./EventActivityRankingPanel";
import DebugErrorBoundary from "@/components/DebugErrorBoundary";
import {
  exportMultiSheetXlsx,
  exportAoaToXlsx,
  type XlsxSheet,
} from "@/lib/utils/exportXlsx";
import type {
  Event,
  EventActivity,
  EventActivityCategory,
  EventRegistration,
  ActivitySubmissionField,
  SubmissionFieldValue,
  SubmissionFileRef,
} from "@/types";

interface Props {
  event: Event;
  registrations: EventRegistration[];
  isAdmin: boolean;
  initialView?: "manage" | "ranking" | "submissions";
  /** Si viene, abre automáticamente el modal de entrega de esa actividad (deep link). */
  initialActivityId?: string;
}

export function EventActivitiesTab({
  event,
  registrations,
  isAdmin,
  initialView = "manage",
  initialActivityId,
}: Props) {
  const { data: categories, isLoading: catLoading } =
    useEventActivityCategories(event.id);
  const { data: activities, isLoading: actLoading } = useEventActivities(
    event.id,
  );
  const { data: scores } = useEventActivityScores(event.id);
  const confirm = useConfirm();

  const [view, setView] = useState<"manage" | "ranking" | "submissions">(
    initialView,
  );
  const profile = useAuthStore((s) => s.profile);
  const directorClub = profile?.directorOfClub;
  // Admin ve todo el evento; el director solo las entregas de su club
  // (menos lecturas + cumple las reglas Firestore endurecidas).
  const { data: eventSubmissions } = useEventSubmissions(
    isAdmin ? event.id : undefined,
  );
  const { data: clubSubmissions } = useClubSubmissions(
    !isAdmin ? event.id : undefined,
    !isAdmin ? directorClub?.id : undefined,
  );
  const submissions = isAdmin ? eventSubmissions : clubSubmissions;
  const [submittingActivity, setSubmittingActivity] =
    useState<EventActivity | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [seeding, setSeeding] = useState(false);
  const [importingCsv, setImportingCsv] = useState(false);
  const [editingCategory, setEditingCategory] =
    useState<EventActivityCategory | null>(null);
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [editingActivity, setEditingActivity] = useState<{
    activity: EventActivity | null;
    category: EventActivityCategory;
  } | null>(null);
  const [scoringActivity, setScoringActivity] = useState<{
    activity: EventActivity;
    category: EventActivityCategory;
  } | null>(null);
  const [batchScoringActivity, setBatchScoringActivity] = useState<{
    activity: EventActivity;
    category: EventActivityCategory;
  } | null>(null);

  // Fuente global de clubes: se usa para completar nombres cuando la submission
  // fue creada por un club que aún NO tiene inscripciones en el evento.
  const { data: allClubsCatalog } = useClubs();

  // Clubes que muestra la matriz de entregables:
  // 1) los inscritos (registrations), y
  // 2) los que YA enviaron alguna submission aunque no estén inscritos
  //    (ej.: informes mensuales no requieren inscripción previa).
  const clubs = useMemo(() => {
    const map = new Map<string, string>();
    registrations.forEach((r) => {
      if (r.clubId && r.clubName) map.set(r.clubId, r.clubName);
    });
    const catalogById = new Map<string, string>();
    (allClubsCatalog ?? []).forEach((c) => catalogById.set(c.id, c.name));
    (submissions ?? []).forEach((s) => {
      if (!s.clubId || map.has(s.clubId)) return;
      const name =
        s.clubName || catalogById.get(s.clubId) || "(club sin nombre)";
      map.set(s.clubId, name);
    });
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [registrations, submissions, allClubsCatalog]);

  const activitiesByCategory = useMemo(() => {
    const map = new Map<string, EventActivity[]>();
    (activities ?? []).forEach((a) => {
      const arr = map.get(a.categoryId) ?? [];
      arr.push(a);
      map.set(a.categoryId, arr);
    });
    map.forEach((arr) => arr.sort((a, b) => a.order - b.order));
    return map;
  }, [activities]);

  const scoresByActivity = useMemo(() => {
    const map = new Map<string, typeof scores>();
    (scores ?? []).forEach((s) => {
      const arr = map.get(s.activityId) ?? [];
      (arr as any[]).push(s);
      map.set(s.activityId, arr);
    });
    return map;
  }, [scores]);

  const nextCategoryOrder =
    Math.max(0, ...(categories ?? []).map((c) => c.order)) + 10;

  // Deep link: si llega `initialActivityId` y el director tiene club, abrir
  // automáticamente el modal de entrega cuando las actividades carguen.
  // Usamos un ref para que solo se dispare UNA vez por id; si el usuario
  // cierra el modal, no debe re-abrirse.
  const consumedInitialIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!initialActivityId) return;
    if (!activities || activities.length === 0) return;
    if (consumedInitialIdRef.current === initialActivityId) return;
    const target = activities.find((a) => a.id === initialActivityId);
    if (!target) return;
    consumedInitialIdRef.current = initialActivityId;
    if (isAdmin) {
      // Admin: expandir la categoría correspondiente para que la encuentre.
      setExpanded((prev) => new Set(prev).add(target.categoryId));
      return;
    }
    if (!directorClub) return;
    setSubmittingActivity(target);
  }, [initialActivityId, activities, directorClub, isAdmin]);

  /** Cierra el modal y, si vino por deep-link, limpia el hash para no re-abrir. */
  const handleCloseSubmissionModal = () => {
    setSubmittingActivity(null);
    if (
      typeof window !== "undefined" &&
      window.location.hash.startsWith("#submissions/")
    ) {
      const newUrl = `${window.location.pathname}${window.location.search}#submissions`;
      window.history.replaceState(null, "", newUrl);
    }
  };

  /** Construye y copia al portapapeles el deep-link a la entrega. */
  const copySubmissionLink = async (activityId: string) => {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const url = `${origin}/events/${event.id}#submissions/${activityId}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Enlace copiado para enviar a los directores");
    } catch {
      // Fallback: prompt
      window.prompt("Copia el enlace:", url);
    }
  };

  const handleExportSubmissions = () => {
    const acts = (activities ?? []).filter(
      (a) => a.submissionRequirements?.enabled,
    );
    if (acts.length === 0) {
      toast.error("No hay actividades con entregables habilitados");
      return;
    }
    const subs = submissions ?? [];

    const fmtDate = (ts: { toDate?: () => Date } | undefined): string => {
      if (!ts || typeof ts.toDate !== "function") return "";
      try {
        return ts.toDate().toISOString();
      } catch {
        return "";
      }
    };
    const fmtFieldValue = (
      val: SubmissionFieldValue | undefined,
      field: ActivitySubmissionField,
    ): string => {
      if (val === null || val === undefined) return "";
      if (field.type === "file") {
        const f = val as SubmissionFileRef;
        return f?.url ? `${f.name || ""} (${f.url})` : "";
      }
      if (field.type === "multi_file") {
        const arr = (val as SubmissionFileRef[]) ?? [];
        return arr.map((f) => `${f.name || ""} (${f.url})`).join(" | ");
      }
      if (Array.isArray(val)) return (val as string[]).join("; ");
      if (typeof val === "boolean") return val ? "Sí" : "No";
      return String(val);
    };

    const safeName = event.name.replace(/[^a-z0-9]+/gi, "_").toLowerCase();
    const stamp = new Date().toISOString().slice(0, 10);
    const filename = `entregables_${safeName}_${stamp}`;

    const sheets: XlsxSheet[] = [];
    for (const act of acts) {
      const fields = (act.submissionRequirements?.fields ?? [])
        .slice()
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      const actSubs = subs.filter((s) => s.activityId === act.id);

      const header = [
        "Club",
        "Estado",
        "Fuera de plazo",
        "Enviado por",
        "Fecha envío",
        "Última edición por",
        "Fecha última edición",
        "Ediciones",
        ...fields.map((f) => f.label || f.id),
      ];

      const subByClub = new Map(actSubs.map((s) => [s.clubId, s]));
      const orderedClubs = (clubs.length > 0 ? clubs : [])
        .slice()
        .sort((a, b) => (a.name || "").localeCompare(b.name || "", "es"));

      const dataRows: (string | number)[][] = [];
      for (const club of orderedClubs) {
        const sub = subByClub.get(club.id);
        dataRows.push([
          club.name,
          sub
            ? SUBMISSION_STATUS_LABEL[sub.status] || sub.status
            : "Sin entrega",
          sub?.isLate ? "Sí" : "",
          sub?.submittedByName ?? "",
          fmtDate(sub?.submittedAt),
          sub?.lastEditedByName ?? "",
          fmtDate(sub?.lastEditedAt),
          sub?.editCount != null ? sub.editCount : "",
          ...fields.map((f) => fmtFieldValue(sub?.values?.[f.id], f)),
        ]);
      }
      for (const sub of actSubs) {
        if (!orderedClubs.some((c) => c.id === sub.clubId)) {
          dataRows.push([
            sub.clubName,
            SUBMISSION_STATUS_LABEL[sub.status] || sub.status,
            sub.isLate ? "Sí" : "",
            sub.submittedByName ?? "",
            fmtDate(sub.submittedAt),
            sub.lastEditedByName ?? "",
            fmtDate(sub.lastEditedAt),
            sub.editCount != null ? sub.editCount : "",
            ...fields.map((f) => fmtFieldValue(sub.values?.[f.id], f)),
          ]);
        }
      }

      sheets.push({
        name: act.name,
        rows: [header, ...dataRows].map((row) => {
          const obj: Record<string, unknown> = {};
          header.forEach((h, i) => {
            obj[h] = row[i];
          });
          return obj;
        }),
        columns: header.map((h) => ({ header: h, key: h })),
      });
    }

    if (sheets.length === 0) {
      exportAoaToXlsx({
        filename,
        aoa: [[`Reporte de entregables - ${event.name}`], ["Sin actividades"]],
      });
    } else {
      exportMultiSheetXlsx(sheets, filename);
    }
    toast.success("Excel descargado");
  };

  const handleSeed = async () => {
    const ok = await confirm({
      title: "Sembrar categorías estándar",
      description:
        "Se crearán las 10 categorías base con sus rubrics estándar (Espirituales, Formación, Deportivos, etc.). Las que ya existan se mantendrán.",
      confirmText: "Sembrar",
      variant: "info",
    });
    if (!ok) return;
    setSeeding(true);
    try {
      await seedStandardCategories(event.id);
      toast.success("Categorías estándar creadas");
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setSeeding(false);
    }
  };

  const handleDeleteCategory = async (cat: EventActivityCategory) => {
    const acts = activitiesByCategory.get(cat.id) ?? [];
    if (acts.length > 0) {
      toast.error(`Tiene ${acts.length} actividad(es). Elimínalas primero.`);
      return;
    }
    const ok = await confirm({
      title: `Eliminar "${cat.name}"`,
      description: "Esta acción no se puede deshacer.",
      confirmText: "Eliminar",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await deleteCategory(cat.id);
      toast.success("Categoría eliminada");
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const handleDeleteActivity = async (act: EventActivity) => {
    const acScores = scoresByActivity.get(act.id) ?? [];
    if ((acScores as any[]).length > 0) {
      const ok = await confirm({
        title: `Eliminar "${act.name}"`,
        description: `Esta actividad tiene ${(acScores as any[]).length} puntaje(s) registrado(s) que también se eliminarán.`,
        confirmText: "Eliminar todo",
        variant: "danger",
      });
      if (!ok) return;
      // Borrar scores primero
      try {
        await Promise.all((acScores as any[]).map((s) => deleteScore(s.id)));
      } catch (e) {
        toast.error("Error al eliminar puntajes");
        return;
      }
    } else {
      const ok = await confirm({
        title: `Eliminar "${act.name}"`,
        description: "Esta acción no se puede deshacer.",
        confirmText: "Eliminar",
        variant: "danger",
      });
      if (!ok) return;
    }
    try {
      await deleteActivity(act.id);
      toast.success("Actividad eliminada");
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  if (catLoading || actLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Toggle vista + acciones */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 sm:p-4 bg-white rounded-xl shadow-md">
        <div className="grid grid-cols-3 sm:flex gap-1.5 sm:gap-2 w-full sm:w-auto">
          <Button
            variant={view === "manage" ? "primary" : "secondary"}
            size="sm"
            onClick={() => setView("manage")}
            className="px-2 sm:px-3 justify-center"
          >
            <ActivityIcon className="w-4 h-4 sm:mr-1" />
            <span className="hidden sm:inline">Categorías y actividades</span>
            <span className="sm:hidden ml-1 text-xs">Actividades</span>
          </Button>
          <Button
            variant={view === "ranking" ? "primary" : "secondary"}
            size="sm"
            onClick={() => setView("ranking")}
            className="px-2 sm:px-3 justify-center"
          >
            <Trophy className="w-4 h-4 sm:mr-1" />
            <span className="hidden sm:inline">Ranking de actividades</span>
            <span className="sm:hidden ml-1 text-xs">Ranking</span>
          </Button>
          <Button
            variant={view === "submissions" ? "primary" : "secondary"}
            size="sm"
            onClick={() => setView("submissions")}
            className="px-2 sm:px-3 justify-center"
          >
            <FileCheck className="w-4 h-4 sm:mr-1" />
            <span className="hidden sm:inline">Entregables</span>
            <span className="sm:hidden ml-1 text-xs">Entregas</span>
          </Button>
        </div>
        {isAdmin && view === "manage" && (
          <div className="flex flex-wrap gap-2">
            {(!categories || categories.length === 0) && (
              <Button
                variant="secondary"
                size="sm"
                onClick={handleSeed}
                disabled={seeding}
              >
                <Sparkles className="w-4 h-4 mr-1" />
                {seeding ? "Sembrando..." : "Sembrar estándar"}
              </Button>
            )}
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setImportingCsv(true)}
            >
              <Upload className="w-4 h-4 mr-1" />
              Importar CSV
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleExportSubmissions}
              title="Exportar todas las respuestas de entregables a Excel"
            >
              <Download className="w-4 h-4 mr-1" />
              Exportar respuestas
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={clubs.length === 0}
              title="Descargar un PDF con las planillas de todas las actividades (para jueces)"
              onClick={() => {
                const sheets: Array<{
                  categoryName: string;
                  activity: EventActivity;
                }> = [];
                [...(categories ?? [])]
                  .sort((a, b) => a.order - b.order)
                  .forEach((cat) => {
                    (activitiesByCategory.get(cat.id) ?? []).forEach((act) => {
                      sheets.push({ categoryName: cat.name, activity: act });
                    });
                  });
                downloadAllActivityScoreSheets({
                  eventName: event.name,
                  sheets,
                  clubs,
                });
              }}
            >
              <FileDown className="w-4 h-4 mr-1" />
              Planillas PDF
            </Button>
            <Can permission="events.write">
              <Button
                variant="primary"
                size="sm"
                onClick={() => setCreatingCategory(true)}
              >
                <Plus className="w-4 h-4 mr-1" />
                Nueva categoría
              </Button>
            </Can>
          </div>
        )}
        {isAdmin && view === "submissions" && (
          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={handleExportSubmissions}
              title="Exportar todas las respuestas de entregables a Excel"
            >
              <Download className="w-4 h-4 mr-1" />
              Exportar respuestas (Excel)
            </Button>
          </div>
        )}
      </div>

      {view === "ranking" && (
        <EventActivityRankingPanel
          eventId={event.id}
          eventName={event.name}
          scores={scores ?? []}
          clubs={clubs}
          categories={categories ?? []}
          activities={activities ?? []}
          isAdmin={isAdmin}
          // Un director de club solo ve su propia información en el ranking;
          // los admins (asociación/unión) ven el ranking completo.
          restrictToClubId={!isAdmin ? directorClub?.id : undefined}
        />
      )}

      {view === "submissions" && (
        <SubmissionsMatrixPanel
          eventId={event.id}
          activities={activities ?? []}
          categories={categories ?? []}
          clubs={clubs}
          submissions={submissions ?? []}
          isAdmin={isAdmin}
          directorClubId={directorClub?.id}
          directorClubName={directorClub?.name}
          onOpenSubmission={(act) => setSubmittingActivity(act)}
        />
      )}

      {view === "manage" && (
        <>
          {(!categories || categories.length === 0) && (
            <EmptyState
              icon={<ActivityIcon className="w-12 h-12 text-gray-400" />}
              title="Sin categorías"
              description={
                isAdmin
                  ? "Crea categorías manualmente o usa 'Sembrar estándar' para cargar las 10 base de FIEL CONQUISTADOR."
                  : "El administrador aún no ha configurado categorías."
              }
            />
          )}

          {categories && categories.length > 0 && (
            <div className="space-y-3">
              {categories.map((cat) => {
                const acts = activitiesByCategory.get(cat.id) ?? [];
                const isOpen = expanded.has(cat.id);
                return (
                  <Card key={cat.id} className="overflow-hidden">
                    <div
                      className="flex items-center justify-between p-4 cursor-pointer"
                      style={{
                        borderLeft: `4px solid ${cat.color ?? "#7c3aed"}`,
                      }}
                      onClick={() => toggle(cat.id)}
                    >
                      <div className="flex items-center gap-3">
                        {isOpen ? (
                          <ChevronDown className="w-4 h-4 text-gray-500" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-gray-500" />
                        )}
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-gray-900">
                              {cat.name}
                            </h3>
                            <Badge variant="default">{acts.length}</Badge>
                            <Badge variant="secondary">
                              {SCORE_MODE_LABEL[cat.defaultScoreMode]}
                            </Badge>
                          </div>
                          {cat.description && (
                            <p className="text-xs text-gray-500 mt-0.5">
                              {cat.description}
                            </p>
                          )}
                        </div>
                      </div>
                      {isAdmin && (
                        <div
                          className="flex gap-1"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Can permission="events.write">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setEditingCategory(cat)}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                          </Can>
                          <Can permission="events.approve">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteCategory(cat)}
                            >
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </Button>
                          </Can>
                        </div>
                      )}
                    </div>

                    {isOpen && (
                      <CardContent className="border-t border-gray-100 bg-gray-50">
                        {acts.length === 0 && (
                          <p className="text-sm text-gray-500 text-center py-4">
                            Sin actividades en esta categoría.
                          </p>
                        )}
                        {acts.length > 0 && (
                          <div className="space-y-2">
                            {acts.map((act) => {
                              const acScores = (scoresByActivity.get(act.id) ??
                                []) as any[];
                              return (
                                <div
                                  key={act.id}
                                  className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200"
                                >
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <h4 className="font-medium text-gray-900">
                                        {act.name}
                                      </h4>
                                      <Badge variant="secondary">
                                        {SCORE_MODE_LABEL[act.scoreMode]}
                                      </Badge>
                                      {acScores.length > 0 && (
                                        <Badge variant="success">
                                          {acScores.length} puntaje(s)
                                        </Badge>
                                      )}
                                    </div>
                                    {act.description && (
                                      <p className="text-xs text-gray-500 mt-1">
                                        {act.description}
                                      </p>
                                    )}
                                  </div>
                                  {isAdmin && (
                                    <div className="flex gap-1 ml-2">
                                      {act.submissionRequirements?.enabled && (
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() =>
                                            copySubmissionLink(act.id)
                                          }
                                          title="Copiar enlace para directores"
                                        >
                                          <LinkIcon className="w-4 h-4 text-blue-600" />
                                        </Button>
                                      )}
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() =>
                                          downloadActivityScoreSheet({
                                            eventName: event.name,
                                            categoryName: cat.name,
                                            activity: act,
                                            clubs,
                                          })
                                        }
                                        disabled={clubs.length === 0}
                                        title="Descargar planilla PDF (para jueces)"
                                      >
                                        <FileDown className="w-4 h-4 text-purple-600" />
                                      </Button>
                                      <Button
                                        variant="secondary"
                                        size="sm"
                                        onClick={() => {
                                          // Modos categóricos → captura batch (1 columna por opción)
                                          // Modos numéricos → modal individual por club
                                          const batchSupported = [
                                            "podium",
                                            "pass_fail",
                                            "fixed",
                                            "timing",
                                          ].includes(act.scoreMode);
                                          if (batchSupported) {
                                            setBatchScoringActivity({
                                              activity: act,
                                              category: cat,
                                            });
                                          } else {
                                            setScoringActivity({
                                              activity: act,
                                              category: cat,
                                            });
                                          }
                                        }}
                                        disabled={clubs.length === 0}
                                        title={
                                          clubs.length === 0
                                            ? "No hay clubes inscritos"
                                            : "Registrar puntaje"
                                        }
                                      >
                                        <Trophy className="w-4 h-4" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() =>
                                          setEditingActivity({
                                            activity: act,
                                            category: cat,
                                          })
                                        }
                                      >
                                        <Edit2 className="w-4 h-4" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() =>
                                          handleDeleteActivity(act)
                                        }
                                      >
                                        <Trash2 className="w-4 h-4 text-red-600" />
                                      </Button>
                                    </div>
                                  )}
                                  {!isAdmin &&
                                    directorClub &&
                                    act.submissionRequirements?.enabled && (
                                      <div className="ml-2">
                                        <Button
                                          variant="secondary"
                                          size="sm"
                                          onClick={() =>
                                            setSubmittingActivity(act)
                                          }
                                          title="Entregar formulario / archivos"
                                        >
                                          <Send className="w-4 h-4 mr-1" />
                                          Mi entrega
                                        </Button>
                                      </div>
                                    )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                        {isAdmin && (
                          <div className="mt-3">
                            <Can permission="events.write">
                              <Button
                                variant="secondary"
                                size="sm"
                                onClick={() =>
                                  setEditingActivity({
                                    activity: null,
                                    category: cat,
                                  })
                                }
                              >
                                <Plus className="w-4 h-4 mr-1" />
                                Nueva actividad
                              </Button>
                            </Can>
                          </div>
                        )}
                      </CardContent>
                    )}
                  </Card>
                );
              })}
            </div>
          )}

          {!isAdmin && (
            <div className="p-3 rounded-xl bg-blue-50 border border-blue-200 text-sm text-blue-800 flex items-start gap-2">
              <Info className="w-4 h-4 mt-0.5" />
              Modo lectura. Solo los administradores pueden editar actividades y
              registrar puntajes.
            </div>
          )}
        </>
      )}

      {/* Modales */}
      <CategoryFormModal
        isOpen={creatingCategory || !!editingCategory}
        onClose={() => {
          setCreatingCategory(false);
          setEditingCategory(null);
        }}
        eventId={event.id}
        category={editingCategory}
        nextOrder={nextCategoryOrder}
      />
      {editingActivity && (
        <ActivityFormModal
          isOpen={true}
          onClose={() => setEditingActivity(null)}
          eventId={event.id}
          category={editingActivity.category}
          activity={editingActivity.activity}
          nextOrder={
            Math.max(
              0,
              ...(
                activitiesByCategory.get(editingActivity.category.id) ?? []
              ).map((a) => a.order),
            ) + 10
          }
        />
      )}
      {scoringActivity && (
        <DebugErrorBoundary label="ActivityScoreModal">
          <ActivityScoreModal
            isOpen={true}
            onClose={() => setScoringActivity(null)}
            eventId={event.id}
            activity={scoringActivity.activity}
            category={scoringActivity.category}
            clubs={clubs}
            existingScores={
              (scoresByActivity.get(scoringActivity.activity.id) ?? []) as any[]
            }
          />
        </DebugErrorBoundary>
      )}
      {batchScoringActivity && (
        <DebugErrorBoundary label="ActivityBatchScoreModal">
          <ActivityBatchScoreModal
            isOpen={true}
            onClose={() => setBatchScoringActivity(null)}
            eventId={event.id}
            activity={batchScoringActivity.activity}
            category={batchScoringActivity.category}
            clubs={clubs}
            existingScores={
              (scoresByActivity.get(batchScoringActivity.activity.id) ??
                []) as any[]
            }
            submissions={(submissions ?? []).filter(
              (s) => s.activityId === batchScoringActivity.activity.id,
            )}
          />
        </DebugErrorBoundary>
      )}
      <CsvImportModal
        open={importingCsv}
        onClose={() => setImportingCsv(false)}
        eventId={event.id}
        existingCategories={categories ?? []}
        onImported={() => {
          // Las suscripciones ya refrescan; nada extra.
        }}
      />
      {submittingActivity && directorClub && (
        <SubmissionFormModal
          isOpen={true}
          onClose={handleCloseSubmissionModal}
          eventId={event.id}
          activity={submittingActivity}
          clubId={directorClub.id}
          clubName={directorClub.name}
          associationId={directorClub.associationId}
          existing={(submissions ?? []).find(
            (s) =>
              s.activityId === submittingActivity.id &&
              s.clubId === directorClub.id,
          )}
        />
      )}
    </div>
  );
}
